// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: one load at a time for a store, and not again within `minGapMs` of the last one unless
// forced. Mounts, focus, gateway READY and polls all ask the same stores to refresh, most of all
// in the seconds after the app loads; they now share one request instead of each sending theirs.

export class CoalescedLoad {
	private inflight: Promise<void> | null = null;
	private lastAt = Number.NEGATIVE_INFINITY;

	constructor(
		private readonly minGapMs: number,
		private readonly now: () => number = Date.now,
	) {}

	/** A load is on its way. */
	get busy(): boolean {
		return this.inflight !== null;
	}

	/** When the last load finished (or -Infinity). */
	get lastLoadedAt(): number {
		return this.lastAt;
	}

	/**
	 * Joins the load on its way, skips when the last one finished under `minGapMs` ago (unless
	 * `force`), otherwise starts `load`.
	 */
	run(load: () => Promise<void>, options: {force?: boolean} = {}): Promise<void> {
		if (this.inflight) return this.inflight;
		if (!options.force && this.now() - this.lastAt < this.minGapMs) return Promise.resolve();
		const request = load().finally(() => {
			this.lastAt = this.now();
			if (this.inflight === request) this.inflight = null;
		});
		this.inflight = request;
		return request;
	}

	/** Sign-out or account switch: the next run loads whatever happened before. */
	reset(): void {
		this.inflight = null;
		this.lastAt = Number.NEGATIVE_INFINITY;
	}
}
